import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { CompanyData } from 'app/containers/Footer/CompanyData'
import { styles, theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { compact, uniqBy } from 'lodash'
import React, { memo } from 'react'

import { PolicyMenuItem, Props as PolicyMenuItemProps } from './PolicyMenuItem'
import { Social } from './Social'

export interface Props {
  cin?: string
  companyName?: string
  email?: string
  facebookURL?: string
  instagramURL?: string
  languageCode: string
  phone?: string
  rea?: string
  registeredOffice?: string
  shareCapital?: string
  policyMenu?: { [key: string]: PolicyMenuItemProps[] }[]
  vat?: string
}

export const Bottom = memo(function Bottom({
  cin,
  companyName,
  email,
  facebookURL,
  instagramURL,
  languageCode,
  phone,
  rea,
  registeredOffice,
  shareCapital,
  policyMenu,
  vat,
}: Props) {
  return (
    <Container dial={4} row space="between" wrap>
      <LeftSide>
        {companyName ? <CompanyName>{companyName}</CompanyName> : null}
        {vat ? (
          <Vat>{`${useVocabularyData('vat', languageCode)}: ${vat}`}</Vat>
        ) : null}
      </LeftSide>

      <Social facebookURL={facebookURL} instagramURL={instagramURL} />

      <RightSide dial={4} row wrap>
        <CompanyData
          cin={cin}
          className="item-company"
          email={email}
          companyName={companyName}
          languageCode={languageCode}
          phone={phone}
          vat={vat}
          rea={rea}
          registeredOffice={registeredOffice}
          shareCapital={shareCapital}
        />

        {policyMenu ? (
          <Policy row wrap>
            {uniqBy(policyMenu, 'title').map((item: any, index) => (
              <PolicyMenuItem key={index} {...item} />
            ))}
          </Policy>
        ) : null}

        <Credits
          aria-label="QNT Hospitality"
          href="https://www.qnt.it"
          rel="noopenet"
          target="_blank"
        >
          {useVocabularyData('credits', languageCode)}
        </Credits>
      </RightSide>
    </Container>
  )
})

const Container = styled(FlexBox)`
  position: relative;
  width: 100%;
  padding: 1.75rem 4.167vw;
  border-top: 0.0625rem solid ${rgba(theme.colors.variants.neutralDark1, 0.3)};
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.5rem;

  @media (max-width: 1199px) {
    padding: 1.5rem 1.875rem 4.5rem;
  }

  .item-social {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`

const LeftSide = styled.div`
  @media (max-width: 1199px) {
    width: 100%;
    text-align: center;
    margin-bottom: 3rem;
  }
`

const RightSide = styled(FlexBox)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  gap: 1.5625rem;

  @media (max-width: 1199px) {
    width: 100%;
    justify-content: center;
    margin-top: 1.25rem;
  }
`

const CompanyName = styled.div`
  ${styles.description.small}
  display: inline-block;
  position: relative;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  line-height: 1.375rem;
  padding-right: 0.625rem;
  margin-right: 0.625rem;

  &:after {
    content: '';
    position: absolute;
    right: 0;
    top: 0.1875rem;
    bottom: 0.1875rem;
    width: 0.125rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
  }
`

const Vat = styled.div`
  ${styles.description.small}
  display: inline-block;

  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  line-height: 1.375rem;
  margin-right: 2.5rem;

  @media (max-width: 1199px) {
    margin-right: 0;
  }
  @media (max-width: 767px) {
    margin: 0 auto 1.25rem;
    text-align: center;
    width: 100%;
  }
`

const Policy = styled(FlexBox)`
  transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
    opacity: 0.6;
  }
`

const Credits = styled.a`
  transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
    opacity: 0.6;
  }
`
